import type { Background, Widget } from './types';

/**
 * How a widget is coloured on screen. The display and the Studio's readability checks both use this, so a warning in
 * the Studio always describes what the TV really shows.
 */

export type RGB = [number, number, number];
export const WHITE: RGB = [255, 255, 255];
export const INK: RGB = [11, 18, 32];
export const MAX_BORDER = 20;

export const hexToRgb = (h: string): RGB => { const n = parseInt(h.replace('#', '').padEnd(6, '0').slice(0, 6), 16); return [(n >> 16) & 255, (n >> 8) & 255, n & 255]; };
export const rgbToHex = (c: RGB) => `#${c.map((v) => Math.max(0, Math.min(255, Math.round(v))).toString(16).padStart(2, '0')).join('').toUpperCase()}`;
export const rgba = (c: RGB, a: number) => `rgba(${c[0]},${c[1]},${c[2]},${+a.toFixed(3)})`;
/** `top` over `bottom` at `alpha` (0–1). */
export const mix = (top: RGB, bottom: RGB, alpha: number): RGB => [0, 1, 2].map((i) => Math.round(top[i]! * alpha + bottom[i]! * (1 - alpha))) as RGB;
const lin = (v: number) => { const c = v / 255; return c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4; };
export const luminance = (c: RGB | string) => { const [r, g, b] = typeof c === 'string' ? hexToRgb(c) : c; return 0.2126 * lin(r) + 0.7152 * lin(g) + 0.0722 * lin(b); };
export const contrastRatio = (a: RGB | string, b: RGB | string) => { const x = luminance(a), y = luminance(b); return (Math.max(x, y) + 0.05) / (Math.min(x, y) + 0.05); };

/** The colour most of the screen's background shows (the gradient's base dominates). Null for photos. */
export function backgroundColour(b: Background): RGB | null {
  if (b.kind === 'image') return null;
  if (b.kind === 'solid') return hexToRgb(b.color);
  return mix(hexToRgb(b.color), hexToRgb(b.color2 ?? '#000000'), 0.3);
}

/** Same rule the display uses to give unfilled widgets their light-background look. */
export function isLightBackground(b: Background): boolean {
  if (b.kind === 'image') return false;
  return luminance(b.color) > 0.55 && (b.kind === 'solid' || luminance(b.color2 ?? '#000000') > 0.4);
}

/** How much of the widget's colour shows through in each style. */
const GLASS_TINT = 0.42;

/** The colour the widget's surface really shows, given the background behind it. */
export function surfaceColour(w: Pick<Widget, 'style' | 'opacity' | 'fill'>, bg: Background): RGB {
  const base = backgroundColour(bg) ?? [24, 28, 36];
  const light = isLightBackground(bg);
  const a = Math.max(0, Math.min(100, w.opacity)) / 100;
  const over = (c: RGB, alpha: number) => mix(c, base, alpha * a);
  if (w.style === 'none') return base;
  if (w.fill) {
    const f = hexToRgb(w.fill);
    return w.style === 'glass' ? over(f, GLASS_TINT) : over(f, 1);
  }
  switch (w.style) {
    case 'glass': return light ? over(WHITE, 0.65) : over(WHITE, 0.11);
    case 'flat': return light ? over(WHITE, 1) : over(hexToRgb('#162033'), 1);
    case '3d': return light ? over(hexToRgb('#F3F6FA'), 1) : over(hexToRgb('#1C2639'), 1);
    case 'legacy': return over(hexToRgb('#06152E'), 1);
  }
}

/**
 * White or near-black for text on `c`. White is kept while it stays comfortably readable at TV sizes (3:1, the
 * large-text standard), so blue highlights keep white text like Apple's; otherwise whichever reads better.
 */
export const autoInk = (c: RGB): RGB => (contrastRatio(WHITE, c) >= 3 || contrastRatio(WHITE, c) >= contrastRatio(INK, c) ? WHITE : INK);

export interface WidgetLook {
  surface: RGB;
  /** Main text colour and its softer tones. */
  ink: RGB;
  /** Highlight colour (next prayer, ticker tag, progress) and the text drawn on it. */
  accent: string;
  accentInk: RGB;
  /** Highlight used as text (e.g. "Isha in 35m"). */
  accentText: string;
  /** Surface colours when the editor chose a widget colour. */
  fill: { base: string; hi: string; lo: string; glassA: string; glassB: string } | null;
}

export function widgetLook(w: Pick<Widget, 'style' | 'opacity' | 'fill' | 'textColor' | 'accent'>, bg: Background): WidgetLook {
  const surface = surfaceColour(w, bg);
  const ink = w.textColor ? hexToRgb(w.textColor) : autoInk(surface);
  // If the highlight would vanish into the widget's own colour, use the text colour instead.
  const acc = hexToRgb(w.accent);
  const accent = contrastRatio(acc, surface) < 1.6 ? rgbToHex(ink) : w.accent;
  const accentRgb = hexToRgb(accent);
  const accentInk = autoInk(accentRgb);
  const accentText = contrastRatio(accentRgb, surface) >= 3 ? accent : rgbToHex(ink);
  let fill: WidgetLook['fill'] = null;
  if (w.fill && w.style !== 'none') {
    const f = hexToRgb(w.fill);
    fill = { base: rgbToHex(f), hi: rgbToHex(mix(WHITE, f, 0.2)), lo: rgbToHex(mix([0, 0, 0], f, luminance(f) > 0.6 ? 0.08 : 0.24)), glassA: rgba(f, 0.55), glassB: rgba(f, 0.3) };
  }
  return { surface, ink, accent, accentInk, accentText, fill };
}
